import axios from 'axios';
import { GIT_PROVIDERS, GitProvider } from '../consts/git';
import { User } from '../network/httpClients/PlatformClient/api.schema';
import { getOIDCToken } from './authentication';
import { getDynamicSetting } from './dynamicSettings';

/**
 * Generates a connect-account authorize URL via the platform backend.
 * The backend keeps the OAuth state in Redis and owns the provider's client ID,
 * scope and callback, so the JWT never travels in the URL.
 * @param provider - The Git provider to link
 * @returns The provider's authorize URL, or empty string on failure
 */
const connectAccountURL = async (provider: GitProvider): Promise<string> => {
  const token = await getOIDCToken();

  if (!token) {
    return '';
  }

  const serverUrl = getDynamicSetting('server_url');
  const redirectUrl = window.location.origin + window.location.pathname;

  try {
    const response = await axios.post(
      `${serverUrl}/auth/v1/connect-account/state`,
      {
        token,
        provider,
        redirectUrl,
      },
    );

    return response.data?.authorizeUrl ?? '';
  } catch {
    return '';
  }
};

export const githubLoginURL = () => connectAccountURL(GIT_PROVIDERS.GITHUB);

export const gitlabLoginURL = () => connectAccountURL(GIT_PROVIDERS.GITLAB);

export const bitbucketLoginURL = () =>
  connectAccountURL(GIT_PROVIDERS.BITBUCKET);

/**
 * Map of Git providers to their corresponding login URL generator functions
 */
export const ProviderToClickEventMap: Partial<
  Record<GitProvider, () => Promise<string>>
> = {
  github: githubLoginURL,
  gitlab: gitlabLoginURL,
  bitbucket: bitbucketLoginURL,
};

/**
 * Checks if a git provider is connected for the current user
 * @param provider - The git provider key
 * @param user - The user object
 * @returns Whether the provider is connected
 */
export const isGitProviderConnected = (
  provider: GitProvider,
  user: User | undefined,
): boolean => {
  if (!user) return false;

  switch (provider) {
    case GIT_PROVIDERS.GITHUB:
      return !!user.githubUserName;
    case GIT_PROVIDERS.GITLAB:
      return !!user.gitlabUserName;
    case GIT_PROVIDERS.BITBUCKET:
      return !!user.bitbucketUserName;
    default:
      return false;
  }
};

/**
 * Gets the username for a connected git provider
 * @param provider - The git provider key
 * @param user - The user object
 * @returns The username for the provider or empty string
 */
export const getGitProviderUsername = (
  provider: GitProvider,
  user: User | undefined,
): string | null | undefined => {
  if (!user) return undefined;

  switch (provider) {
    case GIT_PROVIDERS.GITHUB:
      return user.githubUserName;
    case GIT_PROVIDERS.GITLAB:
      return user.gitlabUserName;
    case GIT_PROVIDERS.BITBUCKET:
      return user.bitbucketUserName;
    default:
      return undefined;
  }
};

/**
 * Rewrites an Azure DevOps PR URL to the canonical web form a browser can open.
 * ADO PR URLs reach us in shapes that don't render as a working link: the REST
 * API form (`.../_apis/git/repositories/{repoId}/pullRequests/{id}`) and web
 * forms with inconsistent casing/pluralization on the `pullrequest` segment.
 * Rewrites both to `.../_git/{repo}/pullrequest/{id}`. Non-ADO URLs (GitHub,
 * GitLab, Bitbucket, Gerrit) contain no such segments and pass through unchanged.
 * @param prUrl - Any PR/MR URL.
 * @returns A URL safe to use as an href.
 */
export const normalizePrUrl = (prUrl: string): string =>
  prUrl
    .replace('/_apis/git/repositories/', '/_git/')
    .replace(/\/pullRequests?\//i, '/pullrequest/');

/**
 * Extracts a short PR label from a PR/MR URL across supported git providers.
 * @param prUrl - A PR/MR URL (GitHub, GitLab, Bitbucket, Azure DevOps, Gerrit).
 * @returns `PR-{number}`, or the original URL when no pattern matches.
 */
export const extractPrLabel = (prUrl: string): string => {
  const match = prUrl.match(
    /\/(?:pulls?|pull-requests|pullrequests?|merge_requests|\+)\/(\d+)/i,
  );

  return match ? `PR-${match[1]}` : prUrl;
};

/**
 * Collapses per-comment discussion URLs down to one entry per distinct PR/MR/change.
 * Rule mining stores every comment discussion URL that contributed to a rule (e.g.
 * GitHub's `#discussion_r…`, GitLab's `#note_…`, Bitbucket's `#comment-…`), so the
 * same PR frequently appears more than once with a different fragment/query.
 * @param prUrls - PR/MR URLs, possibly with multiple entries per PR.
 * @returns One URL per distinct PR, in first-seen order.
 */
export const dedupeByPr = (prUrls: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const prUrl of prUrls) {
    const prKey = prUrl.replace(/[#?].*$/, '');

    if (seen.has(prKey)) continue;

    seen.add(prKey);
    result.push(prUrl);
  }

  return result;
};
